"use client";

import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { FiInfo } from "react-icons/fi";
import { XIcon } from "./icons/XIcon";

interface Position {
  outcome: string;
  outcomeIndex: number;
  shares: number;
  avgPrice: number;
  currentPrice: number;
  invested: number;
  currentValue: number;
}

interface NetPositionsModalProps {
  isOpen: boolean;
  onClose: () => void;
  positions: Position[];
  marketQuestion: string;
  marketImage?: string;
  outcomes: string[];
}

export default function NetPositionsModal({
  isOpen,
  onClose,
  positions,
  marketQuestion,
  marketImage,
  outcomes,
}: NetPositionsModalProps) {
  // Calculate net value for each outcome scenario
  const calculateNetValue = (winningOutcomeIndex: number) => {
    let totalPayout = 0;
    let totalInvested = 0;

    positions.forEach((pos) => {
      totalInvested += pos.invested;

      if (pos.outcomeIndex === winningOutcomeIndex) {
        // This position wins - shares pay out $1 each
        totalPayout += pos.shares;
      }
      // Losing positions pay out $0
    });

    return {
      payout: totalPayout,
      netValue: totalPayout - totalInvested,
      invested: totalInvested,
    };
  };

  const outcomeScenarios = outcomes.map((outcome, index) => ({
    outcome,
    index,
    ...calculateNetValue(index),
  }));

  // Portal to document.body to escape backdrop-filter / transform ancestors
  // that would otherwise trap `position: fixed` inside their containing block.
  const [mounted, setMounted] = useState(false);
  useEffect(() => { setMounted(true); }, []);

  if (!isOpen || !mounted) return null;

  const content = (
    <div className="fixed inset-0 z-[500] flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative w-full max-w-md mx-4 bg-white dark:bg-[#18181D] rounded-[5px] border border-gray-200 dark:border-[#25252B] shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-[#25252B]">
          <h2 className="font-monument text-lg text-gray-900 dark:text-white">
            Net Positions
          </h2>
          <button
            onClick={onClose}
            className="p-2 rounded-[5px] text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-[#0F0F12] transition-colors"
          >
            <XIcon size={20} />
          </button>
        </div>

        {/* Description */}
        <div className="px-4 pt-4">
          <div className="flex items-start gap-2 p-3 rounded-[5px] bg-gray-50 dark:bg-[#0F0F12]/50 border border-gray-200 dark:border-[#25252B]/50">
            <FiInfo className="w-4 h-4 text-[#CCFF00] flex-shrink-0 mt-0.5" />
            <p className="font-satoshi text-xs text-gray-500 dark:text-gray-400">
              See your gains for each outcome scenario based on all your positions.
            </p>
          </div>
        </div>

        {/* Table Header */}
        <div className="grid grid-cols-3 gap-4 px-4 pt-4 pb-2 text-[10px] font-inter-tight font-medium text-gray-400 dark:text-gray-500 uppercase tracking-wide">
          <div>Outcome</div>
          <div className="text-right">Payout</div>
          <div className="text-right">Net Value</div>
        </div>

        {/* Outcome Scenarios */}
        <div className="px-4 pb-4 space-y-2">
          {outcomeScenarios.map((scenario) => {
            const isProfit = scenario.netValue > 0;
            const isLoss = scenario.netValue < 0;

            return (
              <div
                key={scenario.index}
                className="grid grid-cols-3 gap-4 items-center p-3 rounded-[5px] bg-gray-50 dark:bg-[#0F0F12] border border-gray-200 dark:border-[#25252B]"
              >
                {/* Outcome with market image */}
                <div className="flex items-center gap-2">
                  {marketImage && (
                    <img
                      src={marketImage}
                      alt=""
                      className="w-8 h-8 rounded-[5px] object-cover flex-shrink-0"
                    />
                  )}
                  <span className={`font-inter-tight text-sm font-semibold ${
                    scenario.index === 0 ? "text-[#00FF9D]" : "text-[#FF1744]"
                  }`}>
                    {scenario.outcome}
                  </span>
                </div>

                {/* Payout */}
                <div className="text-right">
                  <span className="font-inter-tight text-sm text-gray-900 dark:text-white">
                    ${scenario.payout.toFixed(2)}
                  </span>
                </div>

                {/* Net Value */}
                <div className="text-right">
                  <span className={`font-monument text-sm font-bold ${
                    isProfit
                      ? "text-[#00FF9D]"
                      : isLoss
                        ? "text-[#FF1744]"
                        : "text-gray-900 dark:text-white"
                  }`}>
                    {scenario.netValue >= 0 ? "+" : ""}${scenario.netValue.toFixed(2)}
                  </span>
                </div>
              </div>
            );
          })}
        </div>

        {/* Footer info */}
        <div className="px-4 pb-4">
          <p className="font-satoshi text-[10px] text-gray-400 dark:text-gray-500 text-center">
            Total invested: ${outcomeScenarios[0]?.invested.toFixed(2) || "0.00"}
          </p>
        </div>
      </div>
    </div>
  );

  return createPortal(content, document.body);
}
